import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useContext,
} from 'react';
import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { useGeolocation } from 'react-use';
import Layout from 'layouts/Admin';
import {
  PanelDataType,
  Spinner,
  TabsPanel,
  EmptyState,
  Progressbar,
  Modal,
} from '@design-system';
import { useToast, Kbd } from '@chakra-ui/react';
import { useRouter } from 'hooks/useRouter';
import { useSession } from 'store/session';
import { LifeProof } from './components/LifeProof';
import { StepsHeader } from './components/StepsHeader';
import { SignatureModal } from './components/SignatureModal';
import { statusOfDocument } from './utils/statusOfDocument';
import { MAX_FILE_SIZE } from 'config/constants';
import { resolveTokenV3 } from 'helpers/sessionContext';
import { WLContext } from '@contexts';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { useSentry } from 'hooks/useSentry';
import { useMixpanel } from 'hooks/useMixpanel';
import { SignersList } from './components/SignatureTabs';
import { ObserversTab, ObserverType } from './components/ObserversTab';
import { DocumentSteps } from './CreateDocument';
import { useFetch } from './hooks/useFetch';
import { useFetchInvites } from './hooks/useFetchInvites';
import {
  IDocumentStatus,
  IInvite,
  IInviteRoles,
  IInviteStatus,
  IInviteType,
  ISigner,
} from '@cincel/client';
import { usePatchInvite } from './hooks/usePatchInvite';
import { useDeleteInvite } from './hooks/useDeleteInvite';
import { ERROR_TOAST_DEFAULTS, SUCCESS_TOAST_DEFAULTS } from '@helpers';
import { usePostSignature } from './hooks/usePostSignature';
import { useFetchInvitationToken } from './hooks/useFetchInvitationToken';
// import { useFetchSignedDocument } from './hooks/useFetchSignedDocument';
import { useFetchReminder } from './hooks/useFetchReminder';
import { useFetchIdentityVerificationUrl } from './hooks/useFetchIdentityVerificationUrl';
import { useFetchIdentityVerificationResults } from './hooks/useFetchIdentityVerificationResults';
import { useFetchNOM151 } from './hooks/useFetchNOM151';
import { useFetchFingerprint } from './hooks/useFetchFingerprint';
import { useFetchBlockchain } from './hooks/useFetchBlockchain';
import { useOpenSignedDocument } from './hooks/useOpenSignedDocument';
import { usePostInvite } from './hooks/usePostInvite';
import { useWindowSize } from 'hooks/useWindowSize';
import { useGeneratePCKS7 } from './hooks/useGeneratePCKS7';
import useImageGenerator from 'hooks/useImageGenerator';
import { useFetchCertificate } from './hooks/useFetchCertificate';
import { useFetchPrivateKey } from './hooks/useFetchPrivateKey';
import { PdfViewport } from './components/PdfViewport';
import { useJwtQueryParam } from './hooks/useJwtQueryParam';
import { withExpiredSigner } from './components/withExpiredSigner';
import { isAxiosError, parseAxiosError } from './utils/axios-error-utils';
import { kebabCase, noop, isNil, omitBy, isNaN, result } from 'lodash';
import { MaybeRenderProp, runIfFn } from 'utils';
import { toPng } from 'html-to-image';
type VerificationDecision = 'PASSED' | 'REJECTED' | 'WARNING' | 'NOT_EXECUTED';

const hashAlgorithm = 'sha256';
const SHOW_BLOCKCHAIN = Boolean(
  process.env.REACT_APP_SHOW_BTN_BLOCKCHAIN === 'true',
);

const SignDocument = () => {
  const { t } = useTranslation();
  const { addBreadcrumb, captureException } = useSentry();
  const { trackEvent } = useMixpanel();
  const {
    color: primaryColor,
    fetchWL,
    fetchUserSettings,
    isFetching,
  } = useContext(WLContext);
  const { isLoggedIn, user, currentOrganization, team } = useSession();
  const organizationId = (currentOrganization as any)?.id;
  const { pkcs7 } = useGeneratePCKS7();
  const { generateImage } = useImageGenerator();

  const { query, location, history } = useRouter();
  const toast = useToast();
  const {
    documentId,
    teamId,
    folderId,
    token = undefined,
    invite,
  } = query as any;

  const userEmail = isLoggedIn
    ? (user as any).email
    : (query as any).email?.replace(' ', '+');

  const [file, setFile] = useState<File>();
  const [isInvite] = useState(() =>
    location.pathname.includes('sign_invitation'),
  );
  const [customSignatureLoading, setCustomSignatureLoading] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [currentSignature, setCurrentSignature] = useState();
  const [showLifeProof, setShowLifeProof] = useState(false);
  const [width, setWidth] = useState(0);
  const [startVerification, setStartVerification] = useState(false);
  const [isKycInProgress, setIsKycInProgress] = useState(false);
  const [signatureModal, setSignatureModal] = useState(false);
  const [hasPermissions, setHasPermissions] = useState(false);
  const [isValidatingKyc, setIsValidatingKyc] = useState(false);
  const [isObserver, setIsObserver] = useState(false);
  const [tmpInvitation, setTmpInvitation] = useState<string | undefined>();
  const [userVerified, setUserVerified] = useState(false);
  const [greaterThan20Mb, setGreaterThan20Mb] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState(true);
  const [certificateFile, setCertificateFile] = useState<string>();
  const [keyFile, setKeyFile] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [signing, setSigning] = useState(false);
  const [documentHash, setDocumentHash] = useState<string>();
  const [userCertificate, setUserCertificate] = useState<string>();
  const [userPrivateKey, setUserPrivateKey] = useState<string>();

  const geoLocation = useGeolocation({
    enableHighAccuracy: true,
    maximumAge: 60000,
  });
  const canvasWidth = useRef(null);
  const signatureRef = useRef();
  const privateKeyBuffer = useRef(new ArrayBuffer(0));
  const certificateBuffer = useRef(new ArrayBuffer(0));
  const htmlToPng = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);

  const { mutateAsync: postInvite } = usePostInvite();

  const {
    data: documentDataV3,
    isLoading: isDocumentDataV3Loading,
    refetch: refetchDocument,
  } = useFetch({
    folder: folderId,
    document: documentId,
    team: teamId,
    token,
  });

  const {
    data: documentInvites,
    refetch: refetchInvites,
    isLoading: isLoadingInvites,
  } = useFetchInvites({
    folder: folderId,
    document: documentId,
    team: teamId,
    token,
    enabled: !isDocumentDataV3Loading,
  });

  // Derived states
  const signers = useMemo(() => {
    if (!documentInvites) return [];
    return documentInvites.data.filter(
      (signer) => signer.type === IInviteType.SIGNER,
    );
  }, [documentInvites]);

  const observers = useMemo(() => {
    if (!documentInvites) return [];
    return documentInvites.data
      .filter((observer) => observer.type === IInviteType.OBSERVER)
      .map((ob) => ({
        name: ob.name as string,
        email: ob.email as string,
        id: ob.uuid as string,
      }));
  }, [documentInvites]);

  const currentSigner = useMemo(() => {
    if (!documentDataV3 || !documentInvites || !signers) return undefined;

    if (user) {
      if (signers && signers.length > 0 && documentInvites) {
        const email = (user as any).email;
        const foundSigner: IInvite | undefined = signers.find(
          (f) => f.email === email,
        );

        if (foundSigner) {
          return {
            id: user.id,
            email: user.email,
            name: (user as any).name,
            kind: user.kind ?? 'signer',
            stage: foundSigner.stage as number,
            user_coordinates: foundSigner.user_coordinates,
          };
        }
      }
    } else {
      if (documentInvites) {
        const invitation = documentInvites.data.find((f) => f.uuid === invite);

        if (invitation) {
          return {
            id: invitation.uuid,
            email: invitation.email,
            name: invitation.name,
            kind: invitation?.type ?? 'signer',
            stage: invitation.stage as number,
            user_coordinates: invitation.user_coordinates,
          };
        }
      }
    }
    return undefined;
  }, [documentDataV3, documentInvites, signers, user, invite]);

  const currentInvitation = useMemo(() => {
    if (!currentSigner || typeof documentId !== 'string' || !documentInvites)
      return undefined;

    if (isLoggedIn) {
      const invitation = documentInvites?.data.find(
        (i) => i.email == currentSigner.email,
      );

      if (typeof invitation === 'undefined') {
        return {
          uuid: (Math.floor(Math.random() * 90000) + 10000).toString(),
          email: user.email,
          name: user.name,
          stage: 1,
          status: 'completed',
          document: documentId,
        } as IInvite;
      } else {
        return invitation;
      }
    } else {
      const invitation = documentInvites?.data.find((i) => i.uuid == invite);
      return invitation;
    }
  }, [
    currentSigner,
    documentId,
    documentInvites,
    isLoggedIn,
    user,
    invite,
  ]);

  const { data: invitationToken } = useFetchInvitationToken({
    folder: folderId,
    document: documentId,
    team: teamId,
    invite: currentInvitation?.uuid,
    enabled:
      !isDocumentDataV3Loading &&
      typeof token === 'undefined' &&
      typeof currentInvitation !== 'undefined',
  });

  const { certificate } = useFetchCertificate({
    token,
    email: currentInvitation?.email,
    enabled: Boolean(currentInvitation?.email),
  });

  const { privateKey } = useFetchPrivateKey({
    token,
    email: currentInvitation?.email,
    enabled: Boolean(currentInvitation?.email),
  });

  const { refetch: fetchFingerprint } = useFetchFingerprint(
    documentDataV3?.data?.name,
  );
  const { refetch: fetchBlockchain } = useFetchBlockchain(
    documentDataV3?.data?.name,
  );
  const { refetch: fetchNOM151 } = useFetchNOM151(documentDataV3?.data?.name);

  const { mutateAsync: patchInvite, isLoading: isLoadingPatchInvites } =
    usePatchInvite({ token });

  const { mutateAsync: deleteInvite, isLoading: isLoadingDeleteInvite } =
    useDeleteInvite({ token });

  const { mutateAsync: signDocumentV3 } = usePostSignature({
    token: token ?? invitationToken?.data,
  });

  const { mutateAsync: fetchAndOpenSignedDocument } = useOpenSignedDocument({
    token: token ?? invitationToken?.data,
    documentName: documentDataV3?.data?.name,
  });

  const {
    data: sendReminderData,
    refetch: refetchInvitation,
    isLoading: isSendingReminderLoading,
  } = useFetchReminder({
    folder: folderId,
    document: documentId,
    team: teamId,
    invite: tmpInvitation,
    enabled: typeof tmpInvitation !== 'undefined',
  });

  const { data: verificationUrl, isLoading: isVerificationUrlLoading } =
    useFetchIdentityVerificationUrl({
      folder: folderId,
      document: documentId,
      token: token ?? invitationToken?.data,
      team: teamId,
      invite: currentInvitation?.uuid,
      enabled:
        typeof currentInvitation !== 'undefined' &&
        startVerification &&
        (typeof invitationToken !== 'undefined' ||
          typeof token !== 'undefined'),
    });

  const { data: verificationResults, isError: isVerificationResultError } =
    useFetchIdentityVerificationResults({
      folder: folderId,
      document: documentId,
      token: token ?? invitationToken?.data,
      team: teamId,
      invite: currentInvitation?.uuid,
      enabled:
        typeof currentInvitation !== 'undefined' &&
        typeof verificationUrl !== 'undefined' &&
        startVerification,
    });

  const isSigned = documentDataV3?.data.status === IDocumentStatus.SIGNED;

  const verificationDecision: VerificationDecision = // @ts-ignore
    verificationResults?.data.decision as unknown as VerificationDecision;

  // React.useEffect(() => {
  //   if (verificationDecision !== 'PASSED') {
  //     setStartVerification(false);
  //     return;
  //   }
  // }, [verificationDecision]);

  const widthRef = useRef(0);
  const div = useCallback((node) => {
    if (node !== null) {
      setWidth(node.getBoundingClientRect().width);
      widthRef.current = node.getBoundingClientRect().width;
    }
  }, []);

  const { width: windowWidth, height } = useWindowSize();
  const topSpacing = 196;
  const isDesktop = windowWidth >= 992;

  const handleSignatureClick = () => {
    setSignatureModal(true);
  };

  const handleLifeProofSuccess = () => {
    setStartVerification(false);
    setIsKycInProgress(false);
  };

  const handleValidateKYC = () => {
    setStartVerification(true);
    setShowLifeProof(true);
  };

  /**
   * Advanced signature
   */
  const handleAdvancedSignature = async () => {
    try {
      const hashToSign = documentHash;

      // const binCert = window.atob(certificateFile || '');
      // const binKey = window.atob(keyFile || '');
      const binCert = certificateFile;
      const binKey = keyFile;

      if (!hashToSign) {
        throw new Error('Hash is not defined');
      }

      if (!binCert) {
        throw new Error('Cert is not defined');
      }

      if (!keyFile) {
        throw new Error('Key is not defined');
      }

      let p7b64;
      setSigning(true);
      try {
        p7b64 = pkcs7(hashToSign as string, binCert, binKey, password || '123');
      } catch (error) {
        console.error(
          'error on fn:%s. err:%o, cert:%o, key:%o, pass:%o',
          'pkcs7',
          error,
          binCert,
          binKey,
          password ? password.slice(0, 3).padEnd(7, '*') : 'undef',
        );
      }

      if (typeof p7b64 === 'undefined') {
        try {
          const hashToSign = window.btoa(
            (documentHash as any)
              .match(/\w{2}/g)
              .map(function (a) {
                return String.fromCharCode(parseInt(a, 16));
              })
              .join(''),
          );

          p7b64 = await (window as any).pkcs7FromHash(
            password,
            binKey,
            binCert,
            hashAlgorithm,
            hashToSign,
            true,
          );
        } catch (error) {
          console.error(
            'error on fn:%s. err:%o, cert:%o, key:%o, pass:%o',
            'sd.pkcs7',
            error,
            binCert,
            binKey,
            password ? password.slice(0, 3).padEnd(7, '*') : 'undef',
          );

          const errstr = (error as string).toString();

          if (errstr.includes('llave privada no pudo abrirse')) {
            throw new Error('Wrong private key password.');
          }

          if (
            errstr.includes('certB64Byte is not defined') ||
            errstr.includes('Could not convert certificate from PEM')
          ) {
            throw new Error(
              "Cert format is not known and can't be used for this operation.",
            );
          }

          if (errstr.includes('Error al procesar el formato de llave')) {
            throw new Error(
              "Key format is not known and can't be used for this operation",
            );
          }

          if (errstr.includes('no es consistente')) {
            throw new Error('Cert and Key do not match.');
          }

          if (errstr.includes('no es consistente')) {
            throw new Error('Cert and Key do not match.');
          }

          throw new Error('Cryptographic signature could not be generated');
        }
      }

      if (!p7b64 || typeof p7b64 === 'undefined') {
        throw new Error('Cryptographic signature is undefined');
      }

      if (currentInvitation && htmlToPng.current) {
        let file;
        try {
          file = await generateImage(htmlToPng.current);
        } catch (error) {
          throw new Error('Error generando la firma con el nombre');
        }

        const signature = await signDocumentV3({
          team: (teamId ?? team?.uuid) as string,
          folderId: folderId as string,
          invite: (invite ?? currentInvitation.uuid) as string,
          image: file,
          documentId: documentId as string,
          headers: {
            type: 'advanced',
            pkcs7: p7b64,
          },
        });

        setSigning(false);
        if (signature.status === 201) {
          refetchInvites();
          refetchDocument();
          setSignatureModal(false);
          toast({
            position: 'top-right',
            isClosable: true,
            title: t(translations.SUCCESS.DOCUMENT_SIGNED),
            status: 'success',
          });
          setIsBlocked(false);
        }

        if (isLoggedIn) {
          trackEvent(`User signed the document`, {
            email: userEmail,
            document: documentId,
            organizationId: (currentOrganization as any).id,
            advanced: true,
          });
        } else {
          trackEvent(`Invitee signed the document`, {
            email: userEmail,
            document: documentId,
            advanced: true,
          });
        }
      }
    } catch (error: any) {
      let title;

      if (isAxiosError(error)) {
        const err = parseAxiosError(error);
        const key = err && 'error' in err && `errors.${err.error}`;
        title = t(key ?? translations.ERRORS.PDF_SIGNATURE_GENERAL);
      } else if (error && error.message) {
        title = t(error.message);
      } else {
        title = t(translations.ERRORS.PDF_SIGNATURE_GENERAL);
      }

      toast({
        position: 'top-right',
        isClosable: true,
        title,
        status: 'error',
      });
      captureException(error as Error, {
        user: userEmail,
      });

      setIsBlocked(false);
      setCustomSignatureLoading(false);
      setSigning(false);
    }
  };

  const handleAutograph = async (signatureType = 'autograph') => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    try {
      const text_signature = isSafari
        ? // @ts-ignore
        signatureRef.current?.toDataURL('image/png')
        : await toPng(signatureRef.current, {
          pixelRatio: 1,
        }).catch(() => {
          return null;
        });

      const autograph_signature = text_signature || currentSignature;

      if (
        autograph_signature === null ||
        typeof currentSigner === 'undefined' ||
        typeof currentInvitation === 'undefined' ||
        typeof certificate === 'undefined' ||
        typeof privateKey === 'undefined'
      ) {
        toast({
          ...ERROR_TOAST_DEFAULTS,
          title: translations.ERRORS.PDF_SIGNATURE_GENERAL,
        });
        return;
      }

      setSigning(true);

      const passDef = process.env.REACT_APP_FAB_PASS;
      let p7b64;

      try {
        const hashToSign = documentHash;

        p7b64 = pkcs7(
          hashToSign as string,
          userCertificate,
          userPrivateKey,
          passDef || '123',
        );
      } catch (error) {
        console.error({ error });
      }

      if (typeof p7b64 === 'undefined') {
        try {
          const hashToSign = window.btoa(
            (documentHash as any)
              .match(/\w{2}/g)
              .map(function (a) {
                return String.fromCharCode(parseInt(a, 16));
              })
              .join(''),
          );

          const certDef = window.atob(userCertificate as string);
          const pvKDef = window.atob(userPrivateKey as string);
          p7b64 = await (window as any).pkcs7FromHash(
            passDef,
            pvKDef,
            certDef,
            hashAlgorithm,
            hashToSign,
            true,
          );
        } catch (error) {
          console.error({ error });
        }
      }

      setIsBlocked(true);

      if (typeof p7b64 === 'undefined') {
        // eslint-disable-next-line no-unsafe-finally
        throw new Error('Error firmando ');
      }
      const signatureResponse = await fetch(autograph_signature);
      const currentSignatureBlob = await signatureResponse.blob();

      const file = new File(
        [currentSignatureBlob],
        `signature_${currentInvitation.uuid}`,
        { type: 'image/png' },
      );

      const { error, loading, ...geo } = geoLocation ?? {};
      const _geo = omitBy(geo, isNil);

      const signature = await signDocumentV3({
        team: (teamId ?? team?.uuid) as string,
        folderId: folderId as string,
        invite: (invite ?? currentInvitation.uuid) as string,
        image: file,
        documentId: documentId as string,
        headers: {
          type: signatureType, // ONLY CHANGE: Use signatureType instead of hardcoded 'autograph'
          pkcs7: p7b64,
          ...(Object.keys(_geo).length > 1 && {
            'user-coordinates': JSON.stringify(_geo),
          }),
        },
      });

      setSigning(false);
      if (signature.status === 201) {
        refetchInvites();
        refetchDocument();
        setSignatureModal(false);
        toast({
          position: 'top-right',
          isClosable: true,
          title: t(translations.SUCCESS.DOCUMENT_SIGNED),
          status: 'success',
        });
        setIsBlocked(false);
      }

      if (isLoggedIn) {
        trackEvent(`User signed the document`, {
          email: userEmail,
          document: documentId,
          organizationId: (currentOrganization as any).id,
        });
      } else {
        trackEvent(`Invitee signed the document`, {
          email: userEmail,
          document: documentId,
        });
      }
    } catch (err: any) {
      const status = (err as AxiosError).response?.status.toString();
      console.error(err);
      setSigning(false);

      // toast genérico predeterminado
      let title = t(translations.ERRORS.PDF_SIGNATURE_GENERAL);
      // toast que viene de la API, aún medio genérico
      if (err && err.message) { title = t(err.message); }

      // toast más específico, directo del catálogo
      if (isAxiosError(err)) {
        const msg = parseAxiosError(err);
        const key = msg && 'error' in msg && `errors.${msg.error}`;
        if (key) { title = t(key); }
      }

      if (status?.startsWith('4')) {
        toast({
          position: 'top-right',
          isClosable: true,
          title,
          status: 'error',
        });
      }

      captureException(err as Error, {
        user: userEmail,
      });

      setIsBlocked(false);
      setCustomSignatureLoading(false);
    }
  };
  /**
   * Signature
   */
  const handleMouseUp = () => {
    const canvas: HTMLCanvasElement = signatureRef.current as any;
    if (typeof canvas.toDataURL !== 'undefined')
      setCurrentSignature(canvas.toDataURL('image/png') as any);
  };

  const handleTouchEnd = () => {
    const canvas: HTMLCanvasElement = signatureRef.current as any;
    if (typeof canvas.toDataURL !== 'undefined')
      setCurrentSignature(canvas.toDataURL('image/png') as any);
  };

  /**
   * Send reminder
   *
   * @param id
   * @returns
   */
  const handleSendReminder = async (id) => {
    setTmpInvitation(id);
    refetchInvitation();
  };

  useEffect(() => {
    if (sendReminderData && typeof tmpInvitation !== 'undefined') {
      setTmpInvitation(undefined);
    }
  }, [sendReminderData, tmpInvitation]);

  useEffect(() => {
    const receiveMessage = (event) => {
      if (
        typeof event.data === 'string' &&
        event.data.includes('{') &&
        event.data.includes('}')
      ) {
        const data = window.JSON.parse(event.data);
        if (data.payload.value == 'success') {
          setIsValidatingKyc(true);
        }
      }
    };

    if (showLifeProof) {
      window.addEventListener('message', receiveMessage, false);
    }

    return () => window.removeEventListener('message', receiveMessage, false);
  }, [showLifeProof]);

  useEffect(() => {
    if (certificate && privateKey) {
      const certificateReader = new FileReader();
      certificateReader.onload = function () {
        if (typeof certificateReader.result === 'string') {
          setUserCertificate(certificateReader.result);
        }
      };
      certificateReader.readAsText(certificate.data);

      const privateKeyReader = new FileReader();
      privateKeyReader.onload = function () {
        if (typeof privateKeyReader.result === 'string') {
          setUserPrivateKey(privateKeyReader.result);
        }
      };
      privateKeyReader.readAsText(privateKey.data);
    }
  }, [certificate, privateKey]);

  useEffect(() => {
    if (documentDataV3) {
      if (
        typeof documentDataV3.data.deleted_at !== 'undefined' &&
        typeof documentDataV3.data.deleted_at === 'string' &&
        new Date(documentDataV3.data.deleted_at).toString() !== 'Invalid Date'
      ) {
        toast({
          ...ERROR_TOAST_DEFAULTS,
          title: t(translations.DOCUMENT_PAGES.DOCUMENT_IS_DELETED),
        });
        history.push('/');
      }
      if (documentDataV3.data.doc) {
        setDocumentHash(documentDataV3.data.original_doc_hash);
        const getDocumentFile = async (documentBlob, name) => {
          const documentFile = new File([documentBlob], `${name}.pdf`, {
            type: 'application/pdf',
          });
          setFile(documentFile);
        };

        const fetchDocument = async (isSigned) => {
          const fetchToken = resolveTokenV3(token);
          try {
            const url = documentDataV3.data[
              !isSigned ? 'doc' : 'doc_signed'
            ] as string;

            const config: AxiosRequestConfig = {
              responseType: 'arraybuffer',
              headers: { Authorization: `Bearer ${fetchToken}` },
              onDownloadProgress: (event) => {
                const progress = Math.round((event.loaded * 100) / event.total);
                setProgress(progress);
              },
            };

            const response = await axios.get(url, config);
            const documentBuffer = response.data;

            const documentBlob = new Blob([documentBuffer], {
              type: 'application/pdf',
            });
            const fileSize = documentBlob.size / 1024 / 1024;
            setDownloadingDocument(false);
            if (fileSize > MAX_FILE_SIZE) {
              setGreaterThan20Mb(true);
            }

            await getDocumentFile(documentBlob, documentDataV3?.data?.name);
          } catch (err) {
            console.error(err);
          }
        };

        const isSigned = documentDataV3.data.status === IDocumentStatus.SIGNED;

        fetchDocument(isSigned);
      }
    }
  }, [documentDataV3]);

  useEffect(() => {
    if (documentDataV3 && documentInvites) {
      if (isInvite && typeof teamId === 'string') {
        fetchWL(teamId);
        fetchUserSettings(teamId);
      }

      if (
        statusOfDocument(documentDataV3.data, t) ===
        t(translations.DOCUMENT_PAGES.SIGNED)
      ) {
        setIsBlocked(true);
      }
    }
  }, [documentDataV3, documentInvites]);

  console.log(currentSigner);


  useEffect(() => {
    if (isLoggedIn && user) {
      const isSignerAdmin =
        team?.role === IInviteRoles.ADMIN || team?.role === IInviteRoles.EDITOR;
      if (isSignerAdmin) {
        setHasPermissions(isSignerAdmin as any);
      }
    }
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (documentInvites) {
      if (isLoggedIn) {
        const isSignerAdmin =
          team?.role === IInviteRoles.ADMIN ||
          team?.role === IInviteRoles.EDITOR;
        const isInviteObserver = documentInvites.data.find(
          (invitation) =>
            invitation.email === user.email &&
            invitation.type === IInviteType.OBSERVER &&
            !isSignerAdmin,
        );

        if (isInviteObserver) {
          setIsObserver(true);
        } else {
          const isSignerAdmin =
            team?.role === IInviteRoles.ADMIN ||
            team?.role === IInviteRoles.EDITOR;

          if (isSignerAdmin) {
            setHasPermissions(isSignerAdmin as any);
          }
        }
      } else {
        const isInviteObserver = documentInvites.data.find(
          (invitation) =>
            invitation.uuid === invite &&
            invitation.type === IInviteType.OBSERVER,
        );

        if (isInviteObserver) {
          setIsObserver(true);
        }
      }
    }
  }, [documentInvites]);

  const geolocationRequired = React.useMemo(() => {
    if (!currentSigner) return false;
    return currentSigner.user_coordinates;
  }, [currentSigner]);

  useEffect(() => {
    if (documentDataV3 && documentInvites && signers) {
      if (user) {
        if (signers && signers.length > 0 && documentInvites) {
          const email = (user as any).email;
          const foundSigner: IInvite | undefined = signers.find(
            (f) => f.email === email,
          );

          if (!foundSigner) {
            const foundObserver = documentInvites.data.find(
              (invite) =>
                invite.email === email && invite.type === IInviteType.OBSERVER,
            );

            const canView =
              team?.role === IInviteRoles.ADMIN ||
              team?.role === IInviteRoles.EDITOR ||
              team?.role === IInviteRoles.LECTOR;
            if (typeof foundObserver === 'undefined' && !canView) {
              history.push('/');
            } else {
              const isSignerAdmin =
                team?.role === IInviteRoles.ADMIN ||
                team?.role === IInviteRoles.EDITOR;
              setHasPermissions(isSignerAdmin);
            }
          }
        }
      } else {
        if (documentInvites) {
          const invitation = documentInvites.data.find(
            (f) => f.uuid === invite,
          );

          if (!invitation) {
            const canView =
              team?.role === IInviteRoles.ADMIN ||
              team?.role === IInviteRoles.EDITOR ||
              team?.role === IInviteRoles.LECTOR;
            if (!canView) {
              history.push('/');
            } else {
              setIsObserver(true);
            }
          }
        }
      }
    }
  }, [documentDataV3, documentInvites, signers, user, team, invite, history]);

  /**
   * Handle trackers
   */
  useEffect(() => {
    if (userEmail && documentDataV3) {
      if (isLoggedIn) {
        addBreadcrumb(
          'signpage_user',
          `User ${user.email} with organization ${organizationId}`,
        );
        trackEvent(`User opened the document`, {
          email: userEmail,
          document: documentId,
          organizationId: (currentOrganization as any).id,
        });
      } else {
        addBreadcrumb('signpage_invitee', `Invitee ${userEmail}`);
        trackEvent(`Invitee opened the document`, {
          document: documentId,
          email: userEmail,
        });
      }
    }
  }, [userEmail, documentDataV3]);

  /**
   * Identity verification
   */
  useEffect(() => {
    if (
      typeof currentInvitation !== 'undefined' &&
      verificationDecision === 'PASSED' &&
      currentInvitation?.identity_verification &&
      documentDataV3?.data.status !== IDocumentStatus.SIGNED &&
      currentInvitation.status !== IInviteStatus.COMPLETED
    ) {
      toast({
        ...SUCCESS_TOAST_DEFAULTS,
        title: t(translations.DOCUMENT_PAGES.IDENTITY_SUCCESS),
      });
      setIsKycInProgress(false);
      setStartVerification(false);
      setShowLifeProof(false);
      setUserVerified(true);
    }

    // if (
    //   typeof currentInvitation !== 'undefined' &&
    //   startVerification &&
    //   (isVerificationResultError || verificationDecision !== 'PASSED')
    // ) {
    //   toast({
    //     ...ERROR_TOAST_DEFAULTS,
    //     title: t(translations.DOCUMENT_PAGES.IDENTITY_FAILED),
    //   });
    //
    //   // setShowLifeProof(false);
    //   // setUserVerified(false);
    //   return;
    // }

    if (
      typeof currentInvitation !== 'undefined' &&
      !currentInvitation?.identity_verification &&
      !isVerificationResultError
    ) {
      setUserVerified(true);
    }
  }, [
    verificationDecision,
    verificationResults,
    currentInvitation,
    documentDataV3,
    isVerificationResultError,
    startVerification,
  ]);

  if ((isInvite && isFetching) || isDocumentDataV3Loading) return null;

  const handleDeleteSigner = async (uuid) => {
    try {
      await deleteInvite({
        team: team?.uuid,
        folder: folderId,
        document: documentId,
        invite: uuid,
      });
      await refetchInvites();
    } catch (err) {
      captureException(err as Error);
    }
  };

  const handleEditSigner = async (edited) => {
    try {
      const oldSigners = [...signers];
      const index = oldSigners.findIndex((o) => o.uuid === edited.uuid);

      const emailExists = oldSigners.find(
        (signer) => signer.email === edited.email,
      );

      if (
        emailExists?.email === edited.email &&
        emailExists?.name === edited.name
      ) {
        toast({
          ...ERROR_TOAST_DEFAULTS,
          title: t(translations.ERRORS.EXISTING_EMAIL),
        });
        return;
      }

      if (index > -1 && edited.uuid) {
        const invitation = {
          name: edited.name,
          email: edited.email,
        };

        await patchInvite({
          team: team?.uuid,
          folder: folderId,
          document: documentId,
          invite: edited.uuid,
          inviteData: invitation,
        });
        await refetchInvites();
      }
    } catch (err) {
      captureException(err as Error);
    }
  };

  const handleObservers = async (
    _data: ObserverType | ObserverType[],
    type?: string,
    edited?: ObserverType,
  ) => {
    try {
      if (type === 'create') {
        const emailExists = observers.find(
          (signer) => signer.email === (_data as ObserverType)?.email,
        );

        if (emailExists?.email === (_data as ObserverType)?.email) {
          toast({
            ...ERROR_TOAST_DEFAULTS,
            title: t(translations.ERRORS.EXISTING_EMAIL),
          });
          return;
        }

        await postInvite({
          team: team?.uuid,
          folder: folderId,
          document: documentId,
          payload: {
            name: (_data as ObserverType)?.name,
            email: (_data as ObserverType)?.email,
            type: 'observer',
          },
        });
        await refetchInvites();
      }

      if (type === 'edit' && edited) {
        const index = observers.findIndex((o) => o.id === edited.id);
        const emailExists = observers.find(
          (signer) => signer.email === edited.email,
        );

        if (
          emailExists?.email === edited.email &&
          emailExists?.name === edited.name
        ) {
          toast({
            ...ERROR_TOAST_DEFAULTS,
            title: t(translations.ERRORS.EXISTING_EMAIL),
          });
          return;
        }

        if (index > -1 && edited.id) {
          const invitation = {
            name: edited.name,
            email: edited.email,
          };

          await patchInvite({
            team: team?.uuid,
            folder: folderId,
            document: documentId,
            invite: edited.id,
            inviteData: invitation,
          });
          await refetchInvites();
        }
      }

      if (type === 'delete' && edited) {
        await deleteInvite({
          team: team?.uuid,
          folder: folderId,
          document: documentId,
          invite: edited.id,
        });
        await refetchInvites();
      }
    } catch (err) {
      console.error(err);
      captureException(err as Error, { document: documentDataV3?.data });
    }
  };

  const handleCertificateFile = (evt) => {
    const currFile = evt.target.files[0];

    if (currFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target) {
          certificateBuffer.current = e.target.result as any;
          setCertificateFile(e.target.result as any);
        }
      };
      reader.readAsBinaryString(currFile);
    }
  };

  const handleKeyFile = (evt) => {
    const currFile = evt.target.files[0];

    if (currFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target) {
          privateKeyBuffer.current = e.target.result as any;
          setKeyFile(e.target.result as any);
        }
      };
      reader.readAsBinaryString(currFile);
    }
  };

  const handlePassword = (p) => {
    setPassword(p);
  };

  const isDocumentSigned =
    documentDataV3?.data?.status === IDocumentStatus.SIGNED;

  if (typeof documentDataV3 === 'undefined') return null;

  const panels: PanelDataType[] = [
    {
      id: '1',
      key: 'tabs-signers-tab',
      name: t(translations.COMMON.STATUS),
      target: 'tabs-signers',
      isActive: true,
      component: (
        <SignersList
          currentInvitation={currentInvitation}
          document={documentDataV3?.data}
          signers={signers}
          handleSign={handleSignatureClick}
          validateKYC={handleValidateKYC}
          isKycInProgress={isKycInProgress}
          isBlocked={isBlocked}

          actionsEnabled={!isDocumentSigned && hasPermissions}
          currentSigner={currentSigner}
          handleDeleteSigner={handleDeleteSigner}
          handleEditSigner={handleEditSigner}
          isLoading={signers.length === 0 || signing}
          isReminderLoading={isSendingReminderLoading}
          isAutographDisabled={
            currentInvitation?.status === IInviteStatus.COMPLETED
          }
          hasPermissions={hasPermissions}
          sendReminder={handleSendReminder}
          primaryColor={primaryColor}
          isObserver={isObserver}
          userVerified={userVerified}
        />
      ),
    },
    ...(hasPermissions || isObserver
      ? [
        {
          id: '4',
          key: 'tabs-observers-tab',
          name: t(translations.COMMON.OBSERVERS),
          target: 'tabs-observers',
          isActive: false,
          component: (
            <ObserversTab
              color={primaryColor}
              observers={observers}
              setObservers={handleObservers}
              actionsEnabled={!isDocumentSigned && hasPermissions}
            />
          ),
        },
      ]
      : []),
  ];
  const totalHeight = height - topSpacing;

  return (
    <Layout
      logo={
        documentId
          ? `${process.env.REACT_APP_API_V3}/documents/${documentId}/assets/logo.light.png`
          : undefined
      }
    >
      {hasPermissions && (
        <div className="md:-mt-8">
          <StepsHeader
            primaryColor={primaryColor}
            currentStep={DocumentSteps.sign}
            onClick={() => { }}
          />
        </div>
      )}

      <Modal
        centeredSubmit
        isCloseable={false}
        setShowModal={noop}
        showButtons={false}
        showCancel={false}
        showModal={
          geolocationRequired &&
          (geoLocation?.longitude === null || geoLocation?.latitude === null)
        }
        title={t(translations.DOCUMENT_PAGES.GEOLOCATION_TITLE)}
        width="md:w-7/12 xl:w-6/12">
        <p>
          {t(translations.DOCUMENT_PAGES.GEOLOCATION_REQUIRED_MESSAGE)}
        </p>
        {geoLocation.error ? (
          <p>
            {
              {
                // https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError/code
                1: (
                  <u>
                    {t(translations.DOCUMENT_PAGES.GEOLOCATION_DENIED_MESSAGE)}
                  </u>
                ),
                2: (
                  <u>
                    {t(translations.DOCUMENT_PAGES.GEOLOCATION_UNSTABLE_MESSAGE)}
                  </u>
                ),
                3: (
                  <u>
                    {t(translations.DOCUMENT_PAGES.GEOLOCATION_UNKNOWN_MESSAGE)}
                  </u>
                ),
              }[geoLocation.error.code]
            }
            <br />
            <pre>GeolocationPositionError</pre> <br />
            <pre>{geoLocation.error.message}</pre>
          </p>
        ) : (
          <p>
            {t(translations.DOCUMENT_PAGES.GEOLOCATION_PERMISSION_PROMPT)}
          </p>
        )}
      </Modal>
      <div className="createDocumentContainer w-full max-w-[100vw] px-0 py-8 pt-0 md:px-0 lg:max-w-none">
        <div className="flex flex-wrap md:min-h-[600px]">
          {/* LEFT */}
          <div className="w-full px-2 md:w-4/12 md:px-0 lg:overflow-y-scroll">
            <div
              className="share-document overflow-x-hidden overflow-y-scroll "
              style={{
                ...(isDesktop ? { maxHeight: totalHeight - 60 } : {}),
              }}>
              {isLoadingPatchInvites ||
                isLoadingInvites ||
                isLoadingDeleteInvite ? (
                <Spinner />
              ) : (
                <TabsPanel
                  color={primaryColor}
                  panels={panels}
                  panelClasses="min-h-[300px]"
                />
              )}
            </div>
          </div>
          {/* Right */}
          <div
            ref={div}
            className="w-full max-w-[100vw] px-2 md:w-8/12 md:pl-4 md:pr-0"
            style={{
              ...(isDesktop ? { height: totalHeight } : {}),
            }}>
            {downloadingDocument && typeof file === 'undefined' && (
              <div className="flex h-full items-center justify-center align-middle">
                <EmptyState
                  header={t('signDocument.loading.headline')}
                  description={t('signDocument.loading.tagline', { progress })}
                  tertiaryAction={
                    <Progressbar color={primaryColor} progress={progress} />
                  }
                />
              </div>
            )}
            {file && documentDataV3 && (
              <div className="flex flex-col">
                <div ref={div}>
                  <PdfViewport
                    isSigned={isSigned}
                    name={documentDataV3?.data.name}
                    isCreating={false}
                    parentHeight={totalHeight}
                    primaryColor={primaryColor}
                    isSignatureToolEnabled={false}
                    document={file}
                    canDownload={
                      documentDataV3?.data.status &&
                      documentDataV3?.data.status === IDocumentStatus.SIGNED
                    }
                    downloadAll={() => {
                      const promises = [
                        fetchNOM151,
                        fetchBlockchain,
                        fetchFingerprint,
                        fetchAndOpenSignedDocument,
                      ];
                      const handlePromise = async (fn: () => Promise<any>) => {
                        try {
                          const response = await fn();
                          return { status: 'fulfilled', value: response };
                        } catch (error) {
                          return { status: 'rejected', reason: error };
                        }
                      };

                      Promise.all(promises.map(handlePromise)).then(
                        (responses) => {
                          return responses;
                        },
                      );
                    }}
                    downloadNom151={fetchNOM151}
                    downloadBlockchain={fetchBlockchain}
                    downloadPdf={fetchAndOpenSignedDocument}
                    donwloadFingerprint={fetchFingerprint}
                    greaterThan20Mb={greaterThan20Mb}
                    signers={[
                      {
                        email: currentInvitation?.email,
                        hex: primaryColor,
                        name: currentInvitation?.name,
                        signature_coordinates:
                          currentInvitation?.signature_coordinates,
                        hasSigned: currentInvitation?.status === 'completed',
                      },
                    ]}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="absolute -top-[1500px]">
          <div
            ref={htmlToPng}
            style={{
              fontFamily: 'RionaldoAngelo',
              width: 400,
              height: 246,
            }}>
            <p
              style={{
                fontFamily: 'RionaldoAngelo',
                fontSize: 72,
              }}>
              {' '}
              {currentInvitation?.name}
            </p>
          </div>
        </div>
      </div>
      <LifeProof
        setShowModal={(e) => {
          setShowLifeProof(e);
          // reset
          setIsKycInProgress(false);
          setStartVerification(false);
        }}
        showModal={showLifeProof}
        title={t(translations.MODALS.LIFE_PROOF_TITLE)}
        isLoading={isVerificationUrlLoading}
        redirectUrl={verificationUrl?.data.uri}
        isValidating={isValidatingKyc}
        handleAccept={() => setIsKycInProgress(true)}
        verificationDecision={verificationDecision}
      />

      <SignatureModal
        currentInvitation={currentInvitation}
        borderColor={primaryColor}
        showModal={signatureModal}
        setSignatureModal={setSignatureModal}
        onSuccess={(type = 'autograph') => {
          if (type === 'advanced') return handleAdvancedSignature();
          return handleAutograph(type);
        }}
        handleMouseUp={handleMouseUp}
        handleTouchEnd={handleTouchEnd}
        isLoading={isBlocked}
        isDisabled={
          currentInvitation?.status === IInviteStatus.COMPLETED && isBlocked
        }
        canvasWidth={canvasWidth}
        signatureRef={signatureRef}
        user={user}
        currentSigner={currentSigner}
        isGreaterThan20Mb={greaterThan20Mb}
        handleCertificateFile={handleCertificateFile}
        handleKeyFile={handleKeyFile}
        handlePassword={handlePassword}
      />
    </Layout>
  );
};

type GeolocationState = ReturnType<typeof useGeolocation>;
type GeolocationTrackProps = {
  children?: MaybeRenderProp<GeolocationState>;
};

function GeolocationTrack(props: GeolocationTrackProps) {
  const state = useGeolocation({
    enableHighAccuracy: true,
    maximumAge: 60000,
  });
  const children = runIfFn(props.children, state);

  return <React.Fragment>{children}</React.Fragment>;
}

export default withExpiredSigner(SignDocument);
